import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import * as Sentry from '@sentry/angular';
import { ApiEnvironmentService } from '../services/settings/api-environment.service';

const SUNNY_STREET_API_HOSTS: readonly string[] = [
  'https://api.dev.sunnystreet.com',
  'https://api.sunnystreet.com'
];

/**
 * HTTP interceptor that adds a correlation ID header to outgoing Sunny Street API requests.
 *
 * The correlation ID enables request tracing from frontend → backend → logs:
 * 1. Generate a unique UUID for each request
 * 2. Add X-Correlation-Id header
 * 3. Backend reads the header and includes it in all log lines
 * 4. Backend echoes the header back in the response
 *
 * The header is only added to Sunny Street API URLs. Third-party APIs (e.g. Nominatim)
 * would otherwise trigger a CORS preflight for the non-safelisted X-Correlation-Id header
 * and reject the request.
 */
@Injectable()
export class CorrelationIdInterceptor implements HttpInterceptor {
  private apiEnvironmentService = inject(ApiEnvironmentService);

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!this.isSunnyStreetApiRequest(req.url)) {
      return next.handle(req);
    }

    const correlationId: string = generateCorrelationId();
    this.addSentryBreadcrumb(buildCorrelationBreadcrumb(req, correlationId));

    const clonedReq: HttpRequest<unknown> = req.clone({
      setHeaders: {
        'X-Correlation-Id': correlationId
      }
    });

    return next.handle(clonedReq);
  }

  // Testability seam, same as ModalService.captureToSentry: `@sentry/angular`'s named exports are
  // non-configurable getters under Karma, so specs replace this method instead of spying on Sentry.
  protected addSentryBreadcrumb(breadcrumb: Sentry.Breadcrumb): void {
    try {
      Sentry.addBreadcrumb(breadcrumb);
    } catch {
      // Telemetry must never break a request.
    }
  }

  private isSunnyStreetApiRequest(url: string): boolean {
    const currentApiUrl: string = this.apiEnvironmentService.getCurrentApiUrl();
    if (currentApiUrl && url.startsWith(currentApiUrl)) {
      return true;
    }
    return SUNNY_STREET_API_HOSTS.some((host: string) => url.startsWith(host));
  }
}

/**
 * A breadcrumb per request means every Sentry event carries the correlation ids of the API calls that
 * preceded it, so it can be joined to the backend log lines for those exact requests (SUN-1221). The
 * query string is dropped: the path identifies the endpoint, and query params can carry user data.
 */
function buildCorrelationBreadcrumb(req: HttpRequest<unknown>, correlationId: string): Sentry.Breadcrumb {
  const url: string = req.url.split('?')[0];
  return {
    category: 'http.correlation',
    level: 'info',
    message: `${req.method} ${url}`,
    data: { correlationId, method: req.method, url },
  };
}

// crypto.randomUUID() is secure-context-only, so it throws when the app is served
// from an insecure LAN origin (e.g. `npm run start:external` for mobile testing).
// Fall back to a getRandomValues-based UUID v4 in that case.
function generateCorrelationId(): string {
  const cryptoObj: Crypto | undefined = typeof crypto !== 'undefined' ? crypto : undefined;
  if (cryptoObj?.randomUUID) {
    try {
      return cryptoObj.randomUUID();
    } catch {
      // Fall through to manual v4 generation.
    }
  }
  return generateUuidV4Fallback(cryptoObj);
}

function generateUuidV4Fallback(cryptoObj: Crypto | undefined): string {
  const bytes: Uint8Array = new Uint8Array(16);
  if (cryptoObj?.getRandomValues) {
    cryptoObj.getRandomValues(bytes);
  } else {
    for (let i: number = 0; i < bytes.length; i++) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex: string[] = Array.from(bytes, (b: number) => b.toString(16).padStart(2, '0'));
  return (
    `${hex[0]}${hex[1]}${hex[2]}${hex[3]}-` +
    `${hex[4]}${hex[5]}-` +
    `${hex[6]}${hex[7]}-` +
    `${hex[8]}${hex[9]}-` +
    `${hex[10]}${hex[11]}${hex[12]}${hex[13]}${hex[14]}${hex[15]}`
  );
}
